import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { LuSearch, LuX } from "react-icons/lu";

const RECENT_KEY = "texcoms_recent_searches_v1";

const TRENDING_LINKS = [
  {
    label: "Used Machinery",
    url: "http://texcoms.stage.yectra.com/used-textile-machinery",
  },
  {
    label: "Operational Excellence",
    url: "http://texcoms.stage.yectra.com/textile-operational-excellence",
  },
  {
    label: "Success Stories",
    url: "http://texcoms.stage.yectra.com/success-stories",
  },
  { label: "Articles", url: "http://texcoms.stage.yectra.com/articles" },
];

function SearchOverlay({
  searchTerm,
  setSearchTerm,
  close,
  loading,
  error,
  hasFetched,
  results,
  allResults,
  activeTab,
  setActiveTab,
  categories,
  children,
}) {
  const inputRef = useRef(null);
  const panelRef = useRef(null);
  const trendingRef = useRef(null);

  const [recent, setRecent] = useState([]);
  const [hasTyped, setHasTyped] = useState(false);

  const showNoResults =
    hasFetched &&
    !loading &&
    !error &&
    results.length === 0 &&
    !!searchTerm.trim();

  /** PORTAL ROOT (inside iframe document) */
  const [portalEl] = useState(() => {
    const iframeDoc = document;
    let el = iframeDoc.getElementById("tex-global-search-root");
    if (!el) {
      el = iframeDoc.createElement("div");
      el.id = "tex-global-search-root";
      iframeDoc.body.appendChild(el);
    }
    return el;
  });
  /** Input Auto Focus */
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  /** ESC key closes overlay */
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && close();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [close]);

  /** Set + Search wrapper */
  const setAndSearch = (val) => {
    if (typeof setSearchTerm === "function") {
      setActiveTab("All");
      setSearchTerm(val);
      if (val) setHasTyped(true);
    }
  };

  /** Clear or Close */
  const handleActionButton = () => {
    if (searchTerm.trim()) {
      setAndSearch("");
      inputRef.current?.focus();
    } else {
      close();
    }
  };

  /** Trending Scroll + Drag */
  useEffect(() => {
    const el = trendingRef.current;
    if (!el) return;

    const toggleOverflow = () => {
      el.scrollWidth > el.clientWidth
        ? el.classList.add("trend-scroll-active")
        : el.classList.remove("trend-scroll-active");
    };
    toggleOverflow();

    const ro = new ResizeObserver(toggleOverflow);
    ro.observe(el);

    let drag = false,
      startX = 0,
      startScroll = 0;

    const down = (e) => {
      drag = true;
      startX = e.pageX;
      startScroll = el.scrollLeft;
    };
    const move = (e) => {
      if (!drag) return;
      el.scrollLeft = startScroll - (e.pageX - startX);
    };
    const up = () => (drag = false);

    el.addEventListener("mousedown", down);
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);

    return () => {
      ro.disconnect();
      el.removeEventListener("mousedown", down);
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };
  }, []);

  /** MAIN OVERLAY MARKUP */
  const overlayMarkup = (
    <div className="tex-overlay-root" role="dialog" aria-modal="true">
      <div
        className={`tex-overlay-panel ${
          results.length > 0 ? "has-results" : ""
        }`}
        ref={panelRef}
      >
        {/** HEADER */}
        <header className="tex-overlay-header">
          <div className="tex-overlay-input-row">
            <LuSearch className="tex-overlay-icon" />

            <input
              ref={inputRef}
              className="tex-overlay-input"
              value={searchTerm}
              placeholder="Search..."
              onChange={(e) => setAndSearch(e.target.value)}
            />

            <button
              className="tex-overlay-close"
              type="button"
              onClick={handleActionButton}
            >
              <LuX size={20} />
            </button>
          </div>

          {/** CATEGORY TABS */}
          {categories.length > 0 && searchTerm && (
            <div className="tex-overlay-tabs" role="tablist">
              <button
                className={activeTab === "All" ? "active" : ""}
                onClick={() => setActiveTab("All")}
              >
                All
              </button>

              {categories.map((cat) => (
                <button
                  key={cat}
                  className={activeTab === cat ? "active" : ""}
                  onClick={() => setActiveTab(cat)}
                >
                  {cat}
                </button>
              ))}
            </div>
          )}
        </header>

        {/** MAIN RESULTS */}
        <main
          className={`tex-overlay-results ${
            results.length > 0 ? "expanded" : ""
          }`}
        >
          {/** PRESEARCH (Recent + Trending) */}
          {!searchTerm && !hasTyped && (
            <div className="tex-presearch">
              {recent.length > 0 && (
                <div className="tex-pre-block">
                  <div className="tex-pre-label">Recent</div>
                  <div className="tex-pre-pills">
                    {recent.map((item) => (
                      <button key={item} onClick={() => setAndSearch(item)}>
                        {item}
                      </button>
                    ))}
                    <button
                      className="clear"
                      onClick={() => {
                        setRecent([]);
                        localStorage.removeItem(RECENT_KEY);
                      }}
                    >
                      Clear
                    </button>
                  </div>
                </div>
              )}

              <div className="tex-pre-block">
                <div className="tex-pre-label">Trending</div>
              </div>

              <div
                className="tex-pre-pills trend-nav"
                ref={trendingRef}
                aria-label="Trending links"
              >
                {TRENDING_LINKS.map((item) => (
                  <a
                    key={item.label}
                    href={item.url}
                    target="_parent"
                    className="trend-pill"
                    onClick={() => close()}
                  >
                    {item.label}
                  </a>
                ))}
              </div>
            </div>
          )}

          {/** SEARCH RESULTS */}
          {searchTerm && (
            <>
              {loading && (
                <div className="tex-loading-bar">
                  <span />
                </div>
              )}
              {error && <div className="no-results-box">Error: {error}</div>}
              {showNoResults && (
                <div className="no-results-box">No results found</div>
              )}
              {children}
            </>
          )}
        </main>
      </div>
    </div>
  );

  return createPortal(overlayMarkup, portalEl);
}

export default SearchOverlay;
